import { markChatPanelClosed, scheduleMobileHideRetries } from '../chat-session';
import type { ChatAdapter, ChatLoadContext } from './types';

const SNIPPET_ID = 'livechat-snippet';

/**
 * Exact port of legacy `/assets/bs/js/livechat.js` + official LiveChat ≥2.0 snippet.
 * Queue entries MUST be `["call", ["maximize"]]` / `["on", ["visibility_changed", fn]]`
 * (nested args array) — a flat `["call", "maximize"]` breaks the widget API.
 */
function ensureLiveChatSnippet(license: string): Promise<void> {
  window.__lc = window.__lc || {};
  window.__lc.license = license;

  if (window.LiveChatWidget?._h || document.getElementById(SNIPPET_ID)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const slice = Array.prototype.slice;

    function enqueue(payload: unknown[]) {
      const api = window.LiveChatWidget;
      if (api?._h && typeof (api._h as { apply?: unknown }).apply === 'function') {
        return (api._h as (...a: unknown[]) => unknown).apply(null, payload as never);
      }
      api?._q?.push(payload);
      return undefined;
    }

    const stub: NonNullable<Window['LiveChatWidget']> = {
      _q: [],
      _h: null,
      _v: '2.0',
      on(..._args: unknown[]) {
        enqueue(['on', slice.call(arguments)]);
      },
      once(..._args: unknown[]) {
        enqueue(['once', slice.call(arguments)]);
      },
      off(..._args: unknown[]) {
        enqueue(['off', slice.call(arguments)]);
      },
      get(..._args: unknown[]) {
        if (!stub._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
        return enqueue(['get', slice.call(arguments)]);
      },
      call(..._args: unknown[]) {
        enqueue(['call', slice.call(arguments)]);
      },
      init() {
        const script = document.createElement('script');
        script.id = SNIPPET_ID;
        script.async = true;
        script.type = 'text/javascript';
        script.src = 'https://cdn.livechatinc.com/tracking.js';
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error('Failed to load LiveChat tracking.js'));
        document.head.appendChild(script);
      },
    };

    window.LiveChatWidget = window.LiveChatWidget || stub;
    const lc = window.__lc;
    if (!lc?.asyncInit) {
      stub.init?.();
    } else {
      resolve();
    }
  });
}

/** Ignore a brief `minimized` event some builds emit while opening from `hide`. */
let openingFromFooter = false;

function lcCall(...args: unknown[]): void {
  window.LiveChatWidget?.call(...args);
}

function applyIdentify(username: string | null): void {
  if (!username) return;
  lcCall('set_customer_name', username);
}

/** Legacy: LiveChatWidget.call("hide") */
function hideLauncher(): void {
  lcCall('hide');
}

/**
 * Port of SportsApp.Main.OnLiveChatLoad + LiveChatVisibilityChanged.
 * API commands only — no custom DOM show/hide.
 */
function onReady(ctx: ChatLoadContext): void {
  const widget = window.LiveChatWidget;
  if (!widget) return;

  applyIdentify(ctx.username);

  if (ctx.isMobile) {
    hideLauncher();
    widget.on('visibility_changed', (data: unknown) => {
      const visibility =
        data && typeof data === 'object' && 'visibility' in data
          ? String((data as { visibility: unknown }).visibility)
          : '';
      switch (visibility) {
        case 'maximized':
          openingFromFooter = false;
          ctx.onUnread(0);
          break;
        case 'minimized':
          if (openingFromFooter) return;
          // Legacy: LiveChatWidget.call("hide")
          markChatPanelClosed();
          break;
        case 'hidden':
          break;
        default:
          break;
      }
    });
  }

  widget.on('new_event', () => {
    ctx.onUnread(1);
  });
}

export const livechatAdapter: ChatAdapter = {
  id: 'livechat',

  async load(ctx) {
    await ensureLiveChatSnippet(ctx.key);

    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        onReady(ctx);
        resolve();
      };
      // Official ready hook (also used by LiveChat Hero Guides for initial hide).
      window.LiveChatWidget?.on('ready', finish);
      // Fallback if `ready` already fired before we subscribed.
      window.setTimeout(finish, 100);
    });

    if (ctx.isMobile) {
      scheduleMobileHideRetries(hideLauncher);
    }
  },

  open() {
    // Legacy OpenLiveChat — only maximize opens the full widget from hide/minimize.
    openingFromFooter = true;
    lcCall('maximize');
    window.setTimeout(() => {
      openingFromFooter = false;
    }, 1500);
  },

  hideLauncher,

  identify(username) {
    applyIdentify(username);
  },
};
